/* Recipe quick view (RDB 3.5): opens the quick view sheet from a recipe card.
   Content is fetched on demand from /products/<handle>?section_id=recipe-quick-view
   (one request per recipe, cached in memory for the session) and injected into
   a single shared <dialog>. Mobile: bottom sheet closed by swipe down, Escape,
   Back (a history entry is pushed on open) or the close button. Desktop:
   centred modal. Focus stays trapped while open (native <dialog>) and returns
   to the trigger on close; the URL never changes. If the fetch fails the
   visitor is navigated to the full recipe page instead. */

if ( typeof RecipeQuickViewSheet !== 'function' ) {

	class RecipeQuickViewSheet {

		constructor() {
			this._dialog = null;
			this._content = null;
			this._scroller = null;
			this._closeButton = null;
			this._trigger = null;
			this._popping = false;
			this._fetchController = null;
			this._dragging = false;
		}

		_build() {

			this._dialog = document.createElement('dialog');
			this._dialog.className = 'recipe-quick-view';

			const closeLabel = document.querySelector('[data-recipe-quick-view-close-label]')?.dataset.recipeQuickViewCloseLabel || 'Close';

			this._closeButton = document.createElement('button');
			this._closeButton.type = 'button';
			this._closeButton.className = 'recipe-quick-view__close';
			this._closeButton.setAttribute('aria-label', closeLabel);
			this._closeButton.innerHTML = '<svg aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M1 1l14 14M15 1L1 15"/></svg>';
			this._closeButton.addEventListener('click', () => { this.close(); });

			this._scroller = document.createElement('div');
			this._scroller.className = 'recipe-quick-view__scroller';

			this._content = document.createElement('div');
			this._content.className = 'recipe-quick-view__content';
			this._scroller.appendChild(this._content);

			this._dialog.appendChild(this._closeButton);
			this._dialog.appendChild(this._scroller);
			// Accessible name before any content loads (the labelledby target
			// recipe-qv-title only exists once the content is injected).
			this._dialog.setAttribute('aria-label', document.querySelector('[data-recipe-quick-view-label]')?.dataset.recipeQuickViewLabel || 'Quick view');
			this._dialog.setAttribute('aria-labelledby', 'recipe-qv-title');
			document.body.appendChild(this._dialog);

			// Explicit Tab/Shift+Tab trap: keeps focus inside the sheet even
			// where the native dialog focus containment falls short.
			this._dialog.addEventListener('keydown', e => this._trapFocus(e));

			// Escape is routed through close() so history and focus are handled.
			this._dialog.addEventListener('cancel', e => {
				e.preventDefault();
				this.close();
			});

			// Backdrop tap closes (criterion 4). A click lands on the dialog
			// element itself — never on a child — when it hits the
			// ::backdrop pseudo-element outside the panel.
			this._dialog.addEventListener('click', e => {
				if ( e.target === this._dialog ) {
					this.close();
				}
			});

			// RDB 3.21: every close path (this.close(), Escape, Back, backdrop
			// tap, or a native close) ends in the same full reset — a mid-drag
			// swipe transform, the loading state and the background scroll lock
			// must not survive into the next open, and the sheet must be left
			// with no inline transform that could offset it when reopened.
			this._dialog.addEventListener('close', () => {
				this._resetSwipe();
				this._dialog.setAttribute('aria-busy', 'false');
				this._lockScroll(false);
			});

			// Back button closes (popstate of the entry pushed on open);
			// Forward re-opens the cached sheet without adding a history entry.
			this._onPopState = e => {
				if ( this._popping ) {
					// popstate caused by our own close() history.back()
					this._popping = false;
					return;
				}
				if ( e.state && e.state.recipeQuickView ) {
					if ( ! ( this._dialog && this._dialog.open ) && this._lastOpen ) {
						this.open(this._lastOpen);
					}
				} else {
					this.close(true);
				}
			};
			window.addEventListener('popstate', this._onPopState);

			// Swipe down closes the bottom sheet (only when the sheet is
			// scrolled to its top, so content scrolling still works).
			this._dialog.addEventListener('touchstart', e => this._onTouchStart(e), { passive: true });
			this._dialog.addEventListener('touchmove', e => this._onTouchMove(e), { passive: false });
			this._dialog.addEventListener('touchend', () => this._onTouchEnd());
			this._dialog.addEventListener('touchcancel', () => this._onTouchEnd());

		}

		open(options) {

			this._lastOpen = options;
			this._trigger = options.trigger;
			this._popping = false;

			if ( ! this._dialog ) {
				this._build();
			}

			if ( this._dialog.open && this._currentHandle == options.handle ) {
				return;
			}

			// Push a history entry so Back closes the sheet — but not when we
			// are already sitting on a quick view entry (re-opened by Forward).
			if ( ! ( history.state && history.state.recipeQuickView ) ) {
				history.pushState({ recipeQuickView: true }, '', window.location.href);
			}

			this._currentHandle = options.handle;
			this._lockScroll(true);
			this._setLoading();
			this._dialog.showModal();
			this._load(options.handle, options.url);

		}

		_load(handle, url) {

			const cacheKey = handle || url;

			if ( RecipeQuickViewSheet._cache.has(cacheKey) ) {
				this._render(RecipeQuickViewSheet._cache.get(cacheKey));
				return;
			}

			if ( this._fetchController ) {
				this._fetchController.abort();
			}
			this._fetchController = new AbortController();

			fetch('/products/' + encodeURIComponent(cacheKey) + '?section_id=recipe-quick-view', {
				signal: this._fetchController.signal
			})
				.then(response => {
					if ( ! response.ok ) {
						throw new Error('HTTP ' + response.status);
					}
					return response.text();
				})
				.then(html => {
					RecipeQuickViewSheet._cache.set(cacheKey, html);
					if ( this._dialog.open && this._currentHandle == cacheKey ) {
						this._render(html);
					}
				})
				.catch(error => {
					if ( error.name === 'AbortError' ) {
						return;
					}
					// Fetch failed: fall back to the full recipe page.
					this.close();
					window.location.href = url;
				});

		}

		_setLoading() {
			this._content.innerHTML = '';
			this._dialog.setAttribute('aria-busy', 'true');
			this._closeButton.focus();
		}

		_render(html) {
			this._content.innerHTML = html;
			this._dialog.setAttribute('aria-busy', 'false');
			// P5.8: the sheet's Save control is the recipe page's own snippet, so
			// its Liquid state (aria-pressed, data-signed-in, product id) arrives
			// inside this fetched HTML and only needs binding now that the markup
			// is in the document. Bound per node, and again after every render,
			// because reopening a recipe re-injects the cached sheet.
			if (window.LiberRecipeSave) {
				window.LiberRecipeSave.init(this._content);
			}
			this._closeButton.focus();
		}

		close(fromPopState = false) {

			if ( ! this._dialog || ! this._dialog.open ) {
				return;
			}

			this._dialog.close();
			this._lockScroll(false);
			this._resetSwipe();

			if ( this._fetchController ) {
				this._fetchController.abort();
			}

			// Pop the quick view history entry if we are on one (whatever put
			// us here); the popstate handler skips its own close via _popping.
			if ( ! fromPopState && history.state && history.state.recipeQuickView ) {
				this._popping = true;
				history.back();
			}

			if ( this._trigger && document.contains(this._trigger) ) {
				this._trigger.focus();
			}

		}

		_lockScroll(lock) {
			if ( lock ) {
				// Compensate the scrollbar width so the page does not shift.
				const scrollbar = window.innerWidth - document.documentElement.clientWidth;
				if ( scrollbar > 0 ) {
					document.body.style.paddingRight = scrollbar + 'px';
				}
				document.body.classList.add('recipe-quick-view-lock');
			} else {
				document.body.style.paddingRight = '';
				document.body.classList.remove('recipe-quick-view-lock');
			}
		}

		_onTouchStart(e) {
			if ( e.touches.length !== 1 ) {
				return;
			}
			this._startY = e.touches[0].clientY;
			this._startX = e.touches[0].clientX;
			this._dragging = false;
		}

		_onTouchMove(e) {

			const touch = e.touches[0];
			const dy = touch.clientY - this._startY;
			const dx = Math.abs(touch.clientX - this._startX);

			if ( ! this._dragging ) {
				if ( dy <= 8 || dx > dy || this._scroller.scrollTop > 0 ) {
					return;
				}
				this._dragging = true;
			}

			e.preventDefault();
			const pull = Math.max(dy, 0);
			this._dialog.style.transform = 'translateY(' + Math.round(pull * 0.5) + 'px)';

		}

		_onTouchEnd() {
			if ( this._dragging ) {
				const pull = Math.abs(parseFloat((this._dialog.style.transform.match(/-?\d+/) || [0])[0]));
				this._resetSwipe();
				this._dragging = false;
				if ( pull > 40 ) {
					this.close();
				}
			}
		}

		_resetSwipe() {
			this._dialog.style.transform = '';
		}

		// Explicit focus trap: Tab/Shift+Tab always moves within the dialog
		// (wrapping at the ends), never out to <body> — including where the
		// native <dialog> focus containment falls short.
		_trapFocus(e) {

			if ( e.key !== 'Tab' ) {
				return;
			}

			const focusables = Array.from(
				this._dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])')
			).filter(el => el.getClientRects().length > 0);

			if ( focusables.length === 0 ) {
				return;
			}

			const first = focusables[0];
			const last = focusables[focusables.length - 1];
			const active = document.activeElement;

			if ( e.shiftKey ) {
				if ( ! this._dialog.contains(active) || active === first ) {
					e.preventDefault();
					last.focus();
				}
			} else if ( ! this._dialog.contains(active) || active === last ) {
				e.preventDefault();
				first.focus();
			}

		}

	}

	RecipeQuickViewSheet._cache = new Map();

	// Plain singleton (not a custom element): one shared sheet for the page.
	window.recipeQuickViewSheet = window.recipeQuickViewSheet || new RecipeQuickViewSheet();

}

if ( typeof RecipeQuickView !== 'function' ) {

	const sheet = window.recipeQuickViewSheet;

	class RecipeQuickView extends HTMLElement {

		constructor() {
			super();
			this._trigger = this.querySelector('[data-recipe-quick-view]');
			this._onClick = e => {
				e.preventDefault();
				sheet.open({
					handle: this._trigger.dataset.recipeHandle,
					url: this._trigger.dataset.recipeUrl,
					trigger: this._trigger
				});
			};
		}

		connectedCallback() {
			if ( this._trigger ) {
				this._trigger.addEventListener('click', this._onClick);
			}
		}

		disconnectedCallback() {
			if ( this._trigger ) {
				this._trigger.removeEventListener('click', this._onClick);
			}
		}

	}

	if ( typeof customElements.get('recipe-quick-view') == 'undefined' ) {
		customElements.define('recipe-quick-view', RecipeQuickView);
	}

}
